import { useRedux } from 'hooks';
import { Button, Card, Col, FormLabel, Row } from 'react-bootstrap';
import { FormInput } from '../../../../components';
import { formatDate, nameof } from '../../../../utils';
import { ButtonWithLoading } from 'components/custom/button/ButtonWithLoad';
import { MDIIcon } from '../../../icons/MDIIcons';
import { useForm } from 'react-hook-form';
import useServerPagedData from 'components/table/useServerPagedData';
import { useState } from 'react';
import config from 'config';
import { useModal } from '../../../uikit/hooks';
import ServerPagedTable from '../../../../components/table/ServerPagedTable';
import { SortCodeMappingConfigurationDto, SortContainerDto } from '../dtos/SortContainerDto';
import { Column } from 'react-table';
import { NParcelDto } from '../dtos/NParcelDto';
import { CreateSortContainer } from '../create-new';

type FormInputs = {
    filterBy: string | null;
    search: string | null;
    state: string | null;
};
export const initialFormData: FormInputs = {
    filterBy: 'all',
    search: '',
    state: 'All',
};

const sortType = 'desc';
const sortProperty = 'ObjectUpdated';

const SortContainerListView = () => {
    const { appSelector } = useRedux();

    const { register, handleSubmit, setValue, watch } = useForm<FormInputs>({ defaultValues: initialFormData });

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const { isOpen: isCreating, toggleModal: toggleCreating } = useModal();

    const [searchParams, setSearchParams] = useState<{
        searchProperty?: string;
        searchValue?: string;
        state?: string;
        containerType: string;
    }>({ searchProperty: 'all', state: 'All', containerType: 'sort' });

    const [showParcels, setPacelToShow] = useState<NParcelDto[] | null>(null);

    const styleSortCodeConfigs = (value: string): JSX.Element[] => {
        if (!value || value.trim() === '') {
            return [];
        }

        const metroCodes = value.split(',');
        const rows = [];
        const itemsPerRow = 4; // Define how many items per row you want

        for (let i = 0; i < metroCodes.length; i += itemsPerRow) {
            const rowItems = metroCodes.slice(i, i + itemsPerRow).map((company) => (
                <span key={company.trim()} className="me-1 bg-info badge bg-primary badge-lg mb-1">
                    {company.trim()}
                </span>
            ));
            // Wrap each group of three items in a new div
            rows.push(
                <div key={i} style={{ display: 'block', clear: 'both' }}>
                    {rowItems}
                </div>
            );
        }

        return rows;
    };

    const columns: ReadonlyArray<Column> = [
        {
            Header: 'Container Id',
            accessor: 'container.btcontainerid',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                const btContainerId = (row.original as SortContainerDto).btContainerId;
                return (
                    <div>
                        <div>{btContainerId}</div>

                        <Row className="flex-md-nowrap">
                            <Col xs="auto">
                                <MDIIcon
                                    icon="truck-outline"
                                    onClick={() =>
                                        window.open(
                                            `${window.origin}/labels?trackingNumbers=${btContainerId}`,
                                            '_blank'
                                        )
                                    }
                                    className="primary-color"
                                    size={18}
                                    title="Tracking"
                                />
                            </Col>
                        </Row>
                    </div>
                );
            },
        },
        {
            Header: 'Licence plate',
            accessor: 'licensePlate',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                const licencePlate = (row.original as SortContainerDto)?.licensePlate;
                return (
                    <div>
                        <div>{licencePlate}</div>
                    </div>
                );
            },
        },
        {
            Header: 'In use',
            accessor: 'parcels',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                const parcels = (row.original as SortContainerDto)?.parcels;
                return parcels.length ? (
                    <p
                        style={{
                            paddingTop: '10px',
                            color: '#3C8DBC',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            setPacelToShow(parcels);
                        }}>
                        In Use
                    </p>
                ) : (
                    <p
                        style={{
                            paddingTop: '10px',
                        }}>
                        Not In Use
                    </p>
                );
            },
        },
        {
            Header: 'Configuration',
            accessor: 'sortCodeMappingConfiguration',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                const sortCodeMappings = (value as SortCodeMappingConfigurationDto)?.sortCodeMappings ?? [];

                let sortCodesToRender: string[] = [];

                sortCodeMappings.forEach(m => {
                    if (m.companySortCodes !== undefined) {
                        sortCodesToRender.push(m.companySortCodes.sortCode);
                    }
                });

                return (
                    <>
                        {value != null ? <div>Name: <b>{(value as SortCodeMappingConfigurationDto)?.name}</b></div> : ''}
                        <div>{styleSortCodeConfigs(sortCodesToRender.join(',') || '')}</div>
                    </>
                );
            }
        },
        {
            Header: 'Container label url',
            accessor: 'labelUrl',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                return <a href={value}>Download container label</a>;
            },
        },
        {
            Header: 'Date updated',
            accessor: `${nameof<SortContainerDto>('objectUpdated')}`,
            defaultCanSort: true,
            Cell: ({ value }) => <p>{formatDate(value)}</p>,
        },
    ];

    const {
        tableResult,
        dataLoading,
        serverPageParams,
        setServerPageParams,
        getData,
        useDownloadData,
        resetPageNumber,
        setDataChanged,
    } = useServerPagedData<SortContainerDto>(
        'api/Containers',
        searchParams,
        false,
        'get',
        undefined,
        false,
        undefined,
        sortType,
        sortProperty);

    const onSubmitSearch = (data: FormInputs) => {
        setDataChanged(false);
        resetPageNumber();
        setSearchParams({
            searchProperty: data.filterBy || 'all',
            searchValue: data.search || '',
            state: data?.state?.toString() || 'All',
            containerType: 'sort'
        });
        setDataChanged(true);
    };

    const authToken = appSelector((state) => state.Auth.authorization.token);
    const [loadingDownload, downloadData] = useDownloadData('Containers');

    const handleFilesDownload = async () => {
        const btContainerIds: any = [];
        selectedRows.forEach((rowId) => {
            btContainerIds.push(tableResult.result[rowId]?.btContainerId);
        });

        let btContainerIdsString = btContainerIds.join(',');
        const response = await fetch(`${config.API_URL}/api/Containers/Download/${btContainerIdsString}`, {
            method: 'GET',
            headers: {
                Accept: '*/*',
                Authorization: `Bearer ${authToken}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ContainerLabels.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSelectedRows([]);
    };

    return (
        <>
            <Card>
                <Card.Header>
                    <form onSubmit={handleSubmit(onSubmitSearch)}>
                        <Row className="my-1">
                            <Col xl={5} lg={12}>
                                <Row className="align-items-center">
                                    <Col xs={4}>
                                        <FormLabel className="card-form-label">Filter by:</FormLabel>
                                    </Col>
                                    <Col xs={8}>
                                        <FormInput
                                            name={nameof<FormInputs>('filterBy')}
                                            type="select"
                                            containerAs="row"
                                            register={register}
                                            options={[
                                                { label: 'All', value: 'all' },
                                                {
                                                    label: 'BT container id',
                                                    value: 'btContainerId',
                                                },
                                                {
                                                    label: 'Licence plate',
                                                    value: 'licensePlate',
                                                },
                                                {
                                                    label: 'Label url',
                                                    value: 'labelUrl',
                                                },
                                            ]}
                                            setValue={setValue}
                                            selectValue={watch('filterBy')}
                                            isClearable={false}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row className="my-1">
                            <Col xl={5} lg={12}>
                                <Row className="align-items-center">
                                    <Col xs={4}>
                                        <FormLabel className="card-form-label">Search:</FormLabel>
                                    </Col>
                                    <Col xs={8}>
                                        <FormInput
                                            name={nameof<FormInputs>('search')}
                                            type="input"
                                            register={register}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row className="my-1">
                            <Col xl={5} lg={12}>
                                <Row className="align-items-center">
                                    <Col xs={4}>
                                        <FormLabel className="card-form-label">State:</FormLabel>
                                    </Col>
                                    <Col sm={8}>
                                        <FormInput
                                            name={nameof<FormInputs>('state')}
                                            type="select"
                                            containerAs="row"
                                            register={register}
                                            options={[
                                                { label: 'In Use', value: 'InUse' },
                                                { label: 'All', value: 'All' },
                                                { label: 'Not In Use', value: 'NotInUse' },
                                            ]}
                                            setValue={setValue}
                                            defaultValue={'All'}
                                            selectValue={watch('state')}
                                            isClearable={false}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col className={`d-flex gap-1 justify-content-end mt-1`}>
                                {selectedRows.length > 0 && (
                                    <ButtonWithLoading
                                        isLoading={false}
                                        variant="secondary"
                                        onClick={handleFilesDownload}
                                        className="fw-bold d-flex just-content-center align-items-center">
                                        <MDIIcon
                                            icon="cloud-download-outline"
                                            className="me-1 fs-4"
                                            verticalAlign="middle"
                                        />
                                        Print labels
                                    </ButtonWithLoading>
                                )}
                                <ButtonWithLoading
                                    isLoading={dataLoading}
                                    variant="secondary"
                                    type="submit"
                                    className="fw-bold">
                                    <MDIIcon
                                        icon="magnify"
                                        className="me-1 d-flex just-content-center align-items-center"
                                        verticalAlign="middle"
                                    />
                                    Search
                                </ButtonWithLoading>
                                <Button variant="primary" onClick={toggleCreating} className="fw-bold">
                                    <MDIIcon
                                        icon="plus-box-outline"
                                        className="me-1 fs-4 d-flex just-content-center align-items-center"
                                        verticalAlign="middle"
                                    />
                                    Add new
                                </Button>
                                <ButtonWithLoading
                                    isLoading={loadingDownload}
                                    variant="secondary"
                                    onClick={downloadData}
                                    className="fw-bold d-flex just-content-center align-items-center">
                                    <MDIIcon
                                        icon="cloud-download-outline"
                                        className="me-1 fs-4"
                                        verticalAlign="middle"
                                    />
                                    Export
                                </ButtonWithLoading>
                            </Col>
                        </Row>
                    </form>
                </Card.Header>
                <Card.Body>
                    <ServerPagedTable
                        tableResult={tableResult}
                        dataLoading={dataLoading}
                        serverPageParams={serverPageParams}
                        setServerPageParams={setServerPageParams}
                        columns={columns}
                        isSortable={true}
                        pagination={true}
                        isSelectable
                        setSelectedRowIds={setSelectedRows}
                    />
                </Card.Body>
            </Card>
            {
                isCreating && <CreateSortContainer resetTable={getData} toggleModal={toggleCreating} />
            }
        </>
    );
};

export default SortContainerListView;